/**
 * DSCR Service — Dan's Special Clinical Reference
 *
 * Client-side Firestore operations for DSCR collections:
 * dscrConditions, dscrTools, dscrFactors, dscrToolFactors, dscrScoreThresholds.
 *
 * Also provides formatting helpers for DDX integration:
 * - formatFactorsForClinician (ChartMind)
 * - formatFactorsForPatient (Symptom Checker)
 * - extractDscrClarifyingStructuredItems / extractDscrClarifyingQuestions (Symptom Checker)
 * - Client-side profile cache (5-minute TTL)
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';
import { tokens, clarificationItemsToQuestions } from '@healthdesk/shared-utils';

// ============================================================================
// CONSTANTS
// ============================================================================

export const DSCR_ACCENT_COLOR = tokens.symptomCheck.voiceAccent;

export const DSCR_COLLECTIONS = {
  CONDITIONS: 'dscrConditions',
  TOOLS: 'dscrTools',
  FACTORS: 'dscrFactors',
  TOOL_FACTORS: 'dscrToolFactors',
  SCORE_THRESHOLDS: 'dscrScoreThresholds',
};

export const FACTOR_CATEGORIES = [
  'history',
  'physical_exam',
  'lab',
  'imaging',
  'vital_sign',
  'demographic',
  'risk_factor',
];

export const TOOL_PURPOSES = [
  'rule_in',
  'rule_out',
  'severity',
  'risk_stratify',
  'diagnosis',
];

export const EXTRACTION_STATUSES = {
  PENDING: 'pending',
  EXTRACTED: 'extracted',
  AUTO_EXTRACTED: 'auto-extracted',
  REVIEWED: 'reviewed',
  FAILED: 'failed',
};

// ============================================================================
// CLIENT-SIDE PROFILE CACHE (5-minute TTL)
// ============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000;
const profileCache = new Map();

export function getCachedProfile(conditionName) {
  const key = conditionName?.toLowerCase?.()?.trim();
  if (!key) return null;
  const entry = profileCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    profileCache.delete(key);
    return null;
  }
  return entry.data;
}

export function setCachedProfile(conditionName, data) {
  const key = conditionName?.toLowerCase?.()?.trim();
  if (!key) return;
  profileCache.set(key, { data, timestamp: Date.now() });
}

export function clearProfileCache() {
  profileCache.clear();
}

// ============================================================================
// CONDITIONS
// ============================================================================

/**
 * @param {string} specialty - e.g. 'emergency_medicine'
 * @param {string} [status] - optional filter: 'draft' | 'published'
 * @returns {Promise<Array>}
 */
export async function getConditionsBySpecialty(specialty, status) {
  try {
    let q = db.collection(DSCR_COLLECTIONS.CONDITIONS)
      .where('specialty', '==', specialty);
    if (status) q = q.where('status', '==', status);
    q = q.orderBy('name', 'asc');
    const snap = await q.get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[dscrService] getConditionsBySpecialty:', err);
    return [];
  }
}

/**
 * @param {string} query - search string
 * @returns {Promise<Array>}
 */
export async function searchConditions(query) {
  try {
    const snap = await db.collection(DSCR_COLLECTIONS.CONDITIONS)
      .orderBy('name', 'asc')
      .get();
    const lower = query.toLowerCase();
    return snap.docs
      .map(
        (d) =>
          /** @type {Record<string, any> & { id: string }} */ ({
            id: d.id,
            ...d.data(),
          })
      )
      .filter(
        (c) =>
          c.name?.toLowerCase().includes(lower) ||
          c.icd10Code?.toLowerCase().includes(lower) ||
          c.aliases?.some((a) => a.toLowerCase().includes(lower))
      );
  } catch (err) {
    console.error('[dscrService] searchConditions:', err);
    return [];
  }
}

/**
 * @param {Object} data - condition fields
 * @param {string} [docId] - if updating
 * @returns {Promise<string>} document ID
 */
export async function saveCondition(data, docId) {
  try {
    const now = firebase.firestore.Timestamp.now();
    const payload = { ...data, updatedAt: now };
    if (docId) {
      await db.collection(DSCR_COLLECTIONS.CONDITIONS).doc(docId).set(payload, { merge: true });
      return docId;
    }
    payload.createdAt = now;
    const ref = await db.collection(DSCR_COLLECTIONS.CONDITIONS).add(payload);
    return ref.id;
  } catch (err) {
    console.error('[dscrService] saveCondition:', err);
    throw err;
  }
}

// ============================================================================
// TOOLS
// ============================================================================

/**
 * @param {string} conditionId
 * @param {string} [status]
 * @returns {Promise<Array>}
 */
export async function getToolsByCondition(conditionId, status) {
  try {
    let q = db.collection(DSCR_COLLECTIONS.TOOLS)
      .where('conditionId', '==', conditionId);
    if (status) q = q.where('status', '==', status);
    const snap = await q.get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[dscrService] getToolsByCondition:', err);
    return [];
  }
}

/**
 * @param {Object} data
 * @param {string} [docId]
 * @returns {Promise<string>}
 */
export async function saveTool(data, docId) {
  try {
    const now = firebase.firestore.Timestamp.now();
    const payload = { ...data, updatedAt: now };
    if (docId) {
      await db.collection(DSCR_COLLECTIONS.TOOLS).doc(docId).set(payload, { merge: true });
      return docId;
    }
    payload.createdAt = now;
    const ref = await db.collection(DSCR_COLLECTIONS.TOOLS).add(payload);
    return ref.id;
  } catch (err) {
    console.error('[dscrService] saveTool:', err);
    throw err;
  }
}

// ============================================================================
// FACTORS
// ============================================================================

/**
 * @param {string} toolId
 * @returns {Promise<Array>} joined factor data via dscrToolFactors
 */
export async function getFactorsByTool(toolId) {
  try {
    const tfSnap = await db.collection(DSCR_COLLECTIONS.TOOL_FACTORS)
      .where('toolId', '==', toolId)
      .get();
    if (tfSnap.empty) return [];

    const factorIds = [...new Set(tfSnap.docs.map(d => d.data().factorId))];
    const factorDocs = await Promise.all(
      factorIds.map(fid => db.collection(DSCR_COLLECTIONS.FACTORS).doc(fid).get())
    );
    const factorMap = {};
    factorDocs.forEach(fd => { if (fd.exists) factorMap[fd.id] = fd.data(); });

    return tfSnap.docs.map((d) => {
      const tf = /** @type {Record<string, any> & { id: string }} */ ({
        id: d.id,
        ...d.data(),
      });
      const factor = factorMap[tf.factorId] || {};
      return { ...tf, factorName: factor.factorName, category: factor.category, patientFriendlyQuestion: factor.patientFriendlyQuestion || null };
    });
  } catch (err) {
    console.error('[dscrService] getFactorsByTool:', err);
    return [];
  }
}

/**
 * @param {string} query
 * @returns {Promise<Array>}
 */
export async function searchFactors(query) {
  try {
    const snap = await db.collection(DSCR_COLLECTIONS.FACTORS).get();
    const lower = query.toLowerCase();
    return snap.docs
      .map(
        (d) =>
          /** @type {Record<string, any> & { id: string }} */ ({
            id: d.id,
            ...d.data(),
          })
      )
      .filter((f) => f.factorName?.toLowerCase().includes(lower));
  } catch (err) {
    console.error('[dscrService] searchFactors:', err);
    return [];
  }
}

/**
 * @param {Object} data
 * @param {string} [docId]
 * @returns {Promise<string>}
 */
export async function saveFactor(data, docId) {
  try {
    const now = firebase.firestore.Timestamp.now();
    const payload = { ...data, updatedAt: now };
    if (docId) {
      await db.collection(DSCR_COLLECTIONS.FACTORS).doc(docId).set(payload, { merge: true });
      return docId;
    }
    payload.createdAt = now;
    const ref = await db.collection(DSCR_COLLECTIONS.FACTORS).add(payload);
    return ref.id;
  } catch (err) {
    console.error('[dscrService] saveFactor:', err);
    throw err;
  }
}

/**
 * @param {Object} data
 * @param {string} [docId]
 * @returns {Promise<string>}
 */
export async function saveToolFactor(data, docId) {
  try {
    const now = firebase.firestore.Timestamp.now();
    const payload = { ...data, updatedAt: now };
    if (docId) {
      await db.collection(DSCR_COLLECTIONS.TOOL_FACTORS).doc(docId).set(payload, { merge: true });
      return docId;
    }
    payload.createdAt = now;
    const ref = await db.collection(DSCR_COLLECTIONS.TOOL_FACTORS).add(payload);
    return ref.id;
  } catch (err) {
    console.error('[dscrService] saveToolFactor:', err);
    throw err;
  }
}

// ============================================================================
// THRESHOLDS
// ============================================================================

/**
 * @param {string} toolId
 * @returns {Promise<Array>}
 */
export async function getThresholdsByTool(toolId) {
  try {
    const snap = await db.collection(DSCR_COLLECTIONS.SCORE_THRESHOLDS)
      .where('toolId', '==', toolId)
      .get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[dscrService] getThresholdsByTool:', err);
    return [];
  }
}

/**
 * @param {Object} data
 * @param {string} [docId]
 * @returns {Promise<string>}
 */
export async function saveThreshold(data, docId) {
  try {
    const now = firebase.firestore.Timestamp.now();
    const payload = { ...data, updatedAt: now };
    if (docId) {
      await db.collection(DSCR_COLLECTIONS.SCORE_THRESHOLDS).doc(docId).set(payload, { merge: true });
      return docId;
    }
    payload.createdAt = now;
    const ref = await db.collection(DSCR_COLLECTIONS.SCORE_THRESHOLDS).add(payload);
    return ref.id;
  } catch (err) {
    console.error('[dscrService] saveThreshold:', err);
    throw err;
  }
}

// ============================================================================
// GENERIC DELETE
// ============================================================================

/**
 * @param {string} collection - DSCR_COLLECTIONS value
 * @param {string} docId
 */
export async function deleteDocument(collection, docId) {
  try {
    await db.collection(collection).doc(docId).delete();
  } catch (err) {
    console.error('[dscrService] deleteDocument:', err);
    throw err;
  }
}

// ============================================================================
// FULL CONDITION PROFILE (client-side assembly)
// ============================================================================

/**
 * Assembles the full profile for a condition from client-side Firestore reads.
 * Used by admin UI (ConditionProfilePage).
 * @param {string} conditionId
 * @returns {Promise<Object|null>}
 */
export async function getFullConditionProfile(conditionId) {
  try {
    const condDoc = await db.collection(DSCR_COLLECTIONS.CONDITIONS).doc(conditionId).get();
    if (!condDoc.exists) return null;

    const condition = { id: condDoc.id, ...condDoc.data() };
    const tools = await getToolsByCondition(conditionId);

    const toolsWithDetails = await Promise.all(tools.map(async (tool) => {
      const [factors, thresholds] = await Promise.all([
        getFactorsByTool(tool.id),
        getThresholdsByTool(tool.id),
      ]);
      return { ...tool, factors, thresholds };
    }));

    return { condition, tools: toolsWithDetails };
  } catch (err) {
    console.error('[dscrService] getFullConditionProfile:', err);
    return null;
  }
}

// ============================================================================
// BATCH SAVE (PubMed import / auto-populate)
// ============================================================================

/**
 * Saves a complete extraction result: tool + factors + toolFactors + thresholds.
 * Handles factor deduplication (reuses existing dscrFactors docs when factorName+category match).
 *
 * @param {Object} params
 * @param {string} params.conditionId
 * @param {Object} params.toolData - fields for dscrTools
 * @param {Array} params.factors - [{ factorName, category, direction, weight, ... }]
 * @param {Array} params.thresholds - [{ scoreLow, scoreHigh, riskCategory, ... }]
 * @returns {Promise<string>} toolId
 */
export async function batchSaveExtractedData({ conditionId, toolData, factors = [], thresholds = [] }) {
  try {
    const now = firebase.firestore.Timestamp.now();

    const toolId = await saveTool({ ...toolData, conditionId }, null);

    for (const f of factors) {
      const factorId = await findOrCreateFactor(f.factorName, f.category, f.patientFriendlyQuestion);
      await saveToolFactor({
        toolId,
        conditionId,
        factorId,
        direction: f.direction || 'positive',
        weight: f.weight ?? null,
        weightType: f.weightType || 'additive',
        isMajorCriterion: f.isMajorCriterion || false,
        thresholdDescription: f.thresholdDescription || null,
        notes: f.notes || null,
      });
    }

    for (const t of thresholds) {
      await saveThreshold({ ...t, toolId, conditionId });
    }

    return toolId;
  } catch (err) {
    console.error('[dscrService] batchSaveExtractedData:', err);
    throw err;
  }
}

/**
 * Factor deduplication: looks up existing dscrFactors by factorName + category.
 * Returns existing factorId or creates a new doc.
 */
async function findOrCreateFactor(factorName, category, patientFriendlyQuestion) {
  try {
    const snap = await db.collection(DSCR_COLLECTIONS.FACTORS)
      .where('factorName', '==', factorName)
      .where('category', '==', category)
      .limit(1)
      .get();
    if (!snap.empty) {
      const existing = snap.docs[0];
      if (patientFriendlyQuestion && !existing.data().patientFriendlyQuestion) {
        await db.collection(DSCR_COLLECTIONS.FACTORS).doc(existing.id).update({ patientFriendlyQuestion });
      }
      return existing.id;
    }
    return await saveFactor({ factorName, category, patientFriendlyQuestion: patientFriendlyQuestion || null });
  } catch (err) {
    console.error('[dscrService] findOrCreateFactor:', err);
    return await saveFactor({ factorName, category, patientFriendlyQuestion: patientFriendlyQuestion || null });
  }
}

// ============================================================================
// PUBLISH TOOL ("Accept all & publish")
// ============================================================================

/**
 * One-click: sets the tool + all its toolFactors + thresholds to published/reviewed.
 * @param {string} toolId
 */
export async function publishTool(toolId) {
  try {
    const batch = db.batch();
    const now = firebase.firestore.Timestamp.now();

    batch.update(db.collection(DSCR_COLLECTIONS.TOOLS).doc(toolId), {
      extractionStatus: EXTRACTION_STATUSES.REVIEWED,
      status: 'published',
      updatedAt: now,
    });

    const tfSnap = await db.collection(DSCR_COLLECTIONS.TOOL_FACTORS)
      .where('toolId', '==', toolId).get();
    tfSnap.docs.forEach(d => {
      batch.update(d.ref, { status: 'published', updatedAt: now });
    });

    const thSnap = await db.collection(DSCR_COLLECTIONS.SCORE_THRESHOLDS)
      .where('toolId', '==', toolId).get();
    thSnap.docs.forEach(d => {
      batch.update(d.ref, { status: 'published', updatedAt: now });
    });

    await batch.commit();
  } catch (err) {
    console.error('[dscrService] publishTool:', err);
    throw err;
  }
}

// ============================================================================
// DDX INTEGRATION FORMATTERS
// ============================================================================

/**
 * For ChartMind: formats DSCR digest factors as clinician-facing supporting_factors strings.
 * @param {Object} digestProfile - digest mode response from dscrGetProfile(s)
 * @returns {string[]}
 */
export function formatFactorsForClinician(digestProfile) {
  if (!digestProfile?.topFactors?.length) return [];
  return digestProfile.topFactors.slice(0, 8).map(f => {
    const sign = f.direction === 'negative' ? '' : '+';
    const w = f.weight != null ? ` (${sign}${f.weight})` : '';
    const tool = f.toolName ? `, ${f.toolName}` : '';
    return `[DSCR] ${f.name}${w}${tool}`;
  });
}

/**
 * For Symptom Checker: formats DSCR digest factors as patient-friendly strings.
 * Uses patientFriendlyQuestion; skips factors without patient-friendly text.
 * @param {Object} digestProfile
 * @returns {string[]}
 */
export function formatFactorsForPatient(digestProfile) {
  if (!digestProfile?.topFactors?.length) return [];
  return digestProfile.topFactors
    .filter(f => f.patientFriendlyQuestion)
    .slice(0, 6)
    .map(f => `[DSCR] ${f.patientFriendlyQuestion}`);
}

/**
 * Structured DSCR follow-ups for acute-first merge with LLM clarification_items.
 * @param {Object} profiles - { [conditionName]: digestProfile }
 * @returns {{ question: string, pertains_to_conditions: string[], fromDscr: true }[]}
 */
export function extractDscrClarifyingStructuredItems(profiles) {
  const collected = [];
  const seen = new Set();

  Object.entries(profiles || {}).forEach(([conditionName, profile]) => {
    if (!profile?.dscrEnriched || !profile?.topFactors) return;
    profile.topFactors
      .filter((f) => f.patientFriendlyQuestion && Math.abs(f.weight || 0) >= 1.5)
      .forEach((f) => {
        const key = f.patientFriendlyQuestion.toLowerCase().trim();
        if (seen.has(key)) return;
        seen.add(key);
        collected.push({
          question: f.patientFriendlyQuestion,
          pertains_to_conditions: conditionName ? [conditionName] : [],
          fromDscr: true,
          _weight: Math.abs(f.weight || 0),
        });
      });
  });

  collected.sort((a, b) => (b._weight || 0) - (a._weight || 0));
  return collected.map(({ _weight, ...rest }) => rest);
}

/**
 * For Symptom Checker voice conversation: DSCR items plus LLM strings (no acuity sort).
 * Prefer the caller merging with LLM structured items and running prioritizeClarificationItems once.
 * @param {Object} profiles - { [conditionName]: digestProfile }
 * @param {string[]} existingClarifications - LLM-generated clarification strings
 * @returns {string[]}
 */
export function extractDscrClarifyingQuestions(profiles, existingClarifications = []) {
  const structured = extractDscrClarifyingStructuredItems(profiles);
  const dscrStrings = clarificationItemsToQuestions(structured);

  const norm = (s) => String(s || '').replace(/^\[DSCR\]\s*/i, '').trim().toLowerCase();
  const used = new Set(dscrStrings.map(norm));
  const llmItems = (existingClarifications || [])
    .filter((c) => typeof c === 'string' && c.trim())
    .filter((c) => !used.has(norm(c)))
    .map((c) => ({
      question: c.replace(/^\[DSCR\]\s*/i, '').trim(),
      pertains_to_conditions: [],
      fromDscr: /^\[DSCR\]/i.test(c),
    }));

  return clarificationItemsToQuestions([...structured, ...llmItems]);
}
